import React from "react";

export type SuggestedTransactionPreference =
  | "rent"
  | "water"
  | "electricity"
  | "advancePayments"
  | "internet"
  | "insurance"
  | "renovationValue"
  | "renovationRent"
  | "rentIncrease"
  | "renovationProfitability";

export type HiddenHomeSectionPreference = "watchdog" | "portfolio";

export const hintWindowPreferences = [
  "portfolioOverview",
  "portfolioPerformance",
  "watchdogOverview",
  "watchdogResults",
  "watchdogPriceDevelopment",
  "analyticsViewTip",
  "watchdogFavoritesTip",
  "transactionImportTip",
  "transactionHistoryImportTip",
] as const;

export type HintWindowPreference = (typeof hintWindowPreferences)[number];

export interface UserPreferences {
  hideSuggestedBox: Record<string, SuggestedTransactionPreference[]>;
  mode: "light" | "dark";
  hideSection: HiddenHomeSectionPreference[];
  hintWindows: Record<HintWindowPreference, boolean>;
}

export interface UserInfo {
  token: string;
  email: string;
  role: string;
  membership: string;
  membershipInfo: string;
  maxWatchDogs?: number;
  agreedPrice: string;
  promoCode: string;
  promoDiscountPercent?: number;
  promoFree?: boolean;
  promoLifetime?: boolean;
  validToFormatted: string;
  validTo: string;
  isActive: boolean;
  userName: string;
  idNumber: string;
  address: string;
  subject: string;
  isEligibleForFreeTier: boolean;
  preferences?: UserPreferences;
}

export interface PromoCodeInfo {
  valid: boolean;
  reason?: "INVALID" | "MAX_REDEMPTIONS";
  membership?: string | null;
  maxWatchDogs?: number | null;
  discountPercent?: number;
  free?: boolean;
  lifetime?: boolean;
}

declare global {
  interface Window {
    _gopay: any;
  }
}
export interface ChartDataItem {
  date: string;
  price: number;
  offer: EstateOffer;
}

export interface EstateOffer {
  address: string;
  createdAt: string;
  header: string;
  id: number;
  isReported: boolean;
  isFavorite: boolean;
  isUpdated: boolean;
  mainPicUrl: string;
  offerId: string;
  offerUrl: string;
  origin: string;
  price: string;
  squareMeter: string;
  pricePerSquareMeter: string;
  softiqIndex: number | null;
  comparisonText: string;
  watchdogEntity: {
    id: number;
    client: {};
    isFirstSyncCompleted: boolean;
    isEnabled: boolean;
    doSendUpdated: boolean;
    searchAddress: string;
    offerType: string;
    estateType: string;
  };
}

export type EstateWatchDogAnalysisType =
  | "RENT_CURRENT"
  | "RENT_RENOVATED"
  | "SALE";

export type EstateWatchDogOffer = Omit<EstateOffer, "watchdogEntity"> & {
  analysisType: EstateWatchDogAnalysisType;
};

export interface PublicWatchDogOffer {
  id: number;
  createdAt: string;
  header: string | null;
  address: string | null;
  price: string;
  squareMeter: string | null;
  pricePerSquareMeter: string | null;
  softiqIndex: string | null;
  comparisonText: string | null;
  mainPicUrl: string | null;
  origin?: string | null;
  offerUrl?: string | null;
}

export interface PublicWatchDogOfferGroup {
  watchdogId: number;
  estateType: string;
  offerType?: string | null;
  searchAddress?: string | null;
  offers: PublicWatchDogOffer[];
  favoriteOffers: PublicWatchDogOffer[];
}

export interface PublicReview {
  id?: number;
  name: string;
  stars: number;
  review: string;
  createdAt?: string;
  createdAtFormatted?: string;
}

export interface PublicReviewPayload {
  name: string;
  stars: number;
  review: string;
}
// BLOG

export interface Blog {
  author: string;
  language: string;
  items: BlogPost[];
}

export interface BlogPost {
  id: number | string;
  url: string;
  title: string;
  summary: string | null;
  publish_date: string;
  date_modified: string;
  author?: string;
  language?: string;
  key_takeaways?: string;
  content_html?: string;
  _seo?: BlogSeo;
}

export interface BlogSeo {
  meta_description: string;
  meta_keywords: string[];
}

//WATCHDOG
export type WatchDog = {
  id: number;
  // reporting
  doResetSearch: boolean;
  isNew: boolean;
  client: string | null;
  isFirstSyncCompleted: boolean;
  isEnabled: boolean;
  doEmailReport: boolean;
  emailReportFrequency: string;
  emailReportSetting: string;
  sendOfferAs: string;
  realityUrl: string[];
  // offer
  estateType: string;
  offerType: string;
  flatCategory: string[];
  homeCategory: string[];
  landCategory: string[];
  comercialCategory: string[];
  otherCategory: string[];
  exactAddress: string;
  searchAddress: string;
  inRange: string;
  estateCondition: string[];
  floorFrom: number | null;
  floorTo: number | null;
  usableAreaFrom: number | null;
  usableAreaTo: number | null;
  priceFrom: number | null;
  priceTo: number | null;
  urlSreality: string;
  urlBazos: string;
  urlBezrealitky: string;
  urlIdnesReality: string;
};

export interface FormDataProfile {
  email: string;
  token: string;
  isActive: boolean;
  userName: string | null;
  subject: string | null;
  idNumber: string | null;
  address: string | null;
  oldPassword: string | null;
  newPassword: string | null;
  passwordRepeat: string | null;
  membership: string | null;
}

export interface NewFeatureRequest {
  message: string;
  type: string;
}

export interface FormDataWatchDog extends WatchDog {
  name: string;
}

export interface DesktopNavProps {
  PROFILE_ITEMS: { name: string; href: string; icon: React.ElementType }[];
  NAVBAR_ITEMS: { label: string; href: string; icon: React.ElementType }[];
  ADMIN_ITEMS: { label: string; href: string }[];
  isLoggedIn: boolean;
  setMobileMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  PRODUCTS: {
    name: string;
    description: string;
    href: string;
    icon: React.ElementType;
  }[];
  createNewWatchDog: () => void;
  LOGGED_IN_ITEMS: {
    name: string;
    href: string;
    icon: React.ElementType | null;
  }[];
  OTHER_ITEMS: {
    name: string;
    href: string;
    icon: React.ElementType | null;
  }[];
  handleLogout: () => void;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  formData: FormDataProfile;
  setFormData: React.Dispatch<React.SetStateAction<FormDataProfile>>;
  saveProfileInfo: () => void;
  deleteAccount: () => void;
  userInfo: UserInfo | null;
  handleDiscardChanges: () => void;
  resetPassword: () => void;
  changeMembership: (e: React.FormEvent<HTMLFormElement>) => Promise<void>;
  formDataWatchDog: FormDataWatchDog;
  setFormDataWatchDog: React.Dispatch<React.SetStateAction<FormDataWatchDog>>;
  saveWatchDog: (data?: FormDataWatchDog) => void;
  stepNumber: number;
  setStepNumber: React.Dispatch<React.SetStateAction<number>>;
  resetDetailSearch: () => void;
  selectedItem: ProfileItem | LoggedInItem | null;
  setSelectedItem: React.Dispatch<
    React.SetStateAction<ProfileItem | LoggedInItem | null>
  >;
  isDisabled: boolean;
  mobileMenuOpen: boolean;
  errorMessage: string;
}

export type ProfileItem = DesktopNavProps["PROFILE_ITEMS"][number];
export type LoggedInItem = DesktopNavProps["LOGGED_IN_ITEMS"][number];
export type OtherItem = DesktopNavProps["OTHER_ITEMS"][number];

export type ProfileDialogProps = {
  selectedItem: ProfileItem | LoggedInItem | null;
  handleDiscardChanges: () => void;
  isLoading: boolean;
  formData: FormDataProfile;
  setFormData: React.Dispatch<React.SetStateAction<FormDataProfile>>;
  deleteAccount: () => void;
  saveProfileInfo: () => void;
  changeMembership: (e: React.FormEvent<HTMLFormElement>) => Promise<void>;
  resetPassword: () => void;
  errorMessage?: string | null;
};

export type MembershipInfo = {
  maxWatchDogs: number;
  maxWatchDogsString: string;
};

// MOJE NEMOVITOSTI

export interface TaxReport {
  id: number;
  estate: ClientEstate[];
  year: string;
  taxableIncome: number;
  deductibleExpenses: number;
  otherExpenses: number;
  totalExpenses: number;
  totalTaxableIncome: number;
  totalTax: number;
  taxRate: string;
  dateUpdatedAtFrom: string;
}

export interface ClientEstate extends MyEstate {
  status: string;
  dateTo: string;
  totalProfit: number;
  updatedAt: string;
  accessories: string[];
  transactions: Transaction[];
}

export interface Transaction {
  id: number | null;
  originalId: number;
  name: string;
  amount: number;
  dateFrom: string;
  originalDateFrom: string;
  isPrediction: boolean;
  dateTo: string | null;
  transactionType: string;
  recurrence: string;
  updatedAt: string;
  taxDeductible: boolean;
  showInDashboard: boolean;
  bankImportId?: string | null;
  bankImportSeriesId?: string | null;
  bankImportFileName?: string | null;
  bankImportFormat?: "CSV" | "GPC" | null;
  bankImportTransactionCount?: number | null;
  bankImportPeriods?: string[];
  replaceRecurringTransactionId?: number | null;
  verifiedPeriods?: string[];
  verification?: {
    listOfVerifiedTransactions: string[];
  } | null;
}

export type TransactionPeriodVerification = {
  transactionId: number;
  period: string;
};

export interface ImportedBankTransaction {
  importId: string;
  seriesId: string;
  externalId: string;
  date: string;
  amount: number;
  currency: string;
  transactionType: "INCOME" | "EXPENSE";
  suggestedName: string;
  counterpartyName: string;
  counterpartyAccount: string;
  variableSymbol: string;
  message: string;
  bankTransactionType: string;
  alreadyImported: boolean;
  category:
    | "RENT"
    | "WATER"
    | "ELECTRICITY"
    | "INTERNET"
    | "ADVANCE_PAYMENTS"
    | "INSURANCE"
    | "PROPERTY_TAX"
    | "OTHER";
  existingRecurringTransaction?: {
    id: number;
    name: string;
    amount: number;
    recurrence: string;
    dateFrom: string;
    dateTo: string | null;
  } | null;
  recurringTransactionCandidates?: Array<{
    id: number;
    name: string;
    amount: number;
    recurrence: string;
    dateFrom: string;
    dateTo: string | null;
  }>;
  replaceRecurringTransactionId?: number | null;
  bankImportFileName?: string;
  bankImportFormat?: "CSV" | "GPC";
  bankImportTransactionCount?: number;
  bankImportPeriods?: string[];
}

export interface BankStatementImportResult {
  fileName: string;
  format: "CSV" | "GPC";
  transactionCount: number;
  transactions: ImportedBankTransaction[];
}

export type BankStatementImportActivityInput = {
  fileName: string;
  format: "CSV" | "GPC";
  transactionCount: number;
  periods: string[];
};

export type UserActivity = {
  id: number;
  type: "BANK_STATEMENT_IMPORT" | "TRANSACTION_REMINDER_EMAIL";
  createdAt: string;
  metadata: {
    recipient?: string;
    subject?: string;
    estateId?: number;
    estateName?: string;
    transactionId?: number;
    transactionName?: string;
    amount?: number;
    period?: string;
    fileName?: string;
    format?: "CSV" | "GPC";
    transactionCount?: number;
    periods?: string[];
    htmlBody?: string;
  };
};

export type TransactionPreset = Partial<
  Pick<
    Transaction,
    | "id"
    | "originalId"
    | "name"
    | "amount"
    | "transactionType"
    | "recurrence"
    | "dateFrom"
    | "originalDateFrom"
    | "dateTo"
    | "isPrediction"
    | "taxDeductible"
    | "showInDashboard"
    | "bankImportId"
    | "bankImportSeriesId"
    | "bankImportFileName"
    | "bankImportFormat"
    | "bankImportTransactionCount"
    | "bankImportPeriods"
    | "replaceRecurringTransactionId"
    | "verifiedPeriods"
  >
>;

export type EstateCardProp = {
  estateOffer: EstateOffer;
  isMainPage: boolean;
  imageHeightClass?: string;
  editSpecificResult: (
    id: number,
    isFavorite: boolean,
    doHide: boolean,
  ) => void | Promise<void>;
};

export type MyEstate = {
  id: number;
  name: string;
  estateType: string;
  flatCategory: string;
  exactAddress: string;
  address: string;
  dateFrom: string;
  dateTo: string;
  ownerShip: string;
  estateCondition: string;
  accessories: string[];
  usableArea: number | null;
  purchasePrice: number | null;
  note: string;
  //   tenant
  tenantName: string;
  tenantPhone: string;
  tenantEmail: string;
  tenantNote: string;
  //   property manager
  managerName: string;
  managerPhone: string;
  managerEmail: string;
  managerNote: string;
  //watchdog
  doResetWatchDog: boolean;
};

export interface FormDataMyEstate extends MyEstate {
  isNew: boolean;
}

export interface GraphTransaction {
  year: string;
  name: string;
  amount: number;
  type: "INCOME" | "EXPENSE";
}

export type YearGraphRow = {
  year: string;
  income: number;
  expense: number;
  profit: number;
};

export type YearStats = {
  income: number;
  expense: number;
  profit: number;
  profitTotal: number;
};

export type EstateStats = {
  currentYearStats: YearStats;
  lastYearStats: {
    profit: number;
  };
};

export interface Log {
  id: number;
  createdDate: string;
  createdDateTime: string;
  body: string;
}

export interface ReportEmail {
  id: number;
  createdDateTime: string;
  emailTo: string;
  emailFrom: string;
  subject: string;
  body?: string;
}

export interface TestReport {
  id: number;
  emailTo: string;
  emailSubject: string;
  result: string;
  createdAt: string;
  startedAt: string;
  finishedAt: string;
  duration: number;
  durationSeconds: number;
  profile: string;
  successCount: number;
  errorCount: number;
  totalCount: number;
}

export type EstateStatsMap = Record<string, EstateStats>;
